import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Link, Title, Button } from "react_reusable_components";
import AddQuestionnaireMainContainer from "../../containers/AddQuestionnaireMainContainer/AddQuestionnaireMainContainer";
import AddQuestionnaireMobileContainer from "../../containers/AddQuestionnaireMobileContainer";
import useResponsive from "../../core/hooks/useResponsive";
import { TwoRow } from "../../core/layouts";
import { AuthContext } from "../../globalContext/auth/authProvider";
import {
  getIsAudit,
  getLabelText,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import variables from "../../themes/base/styles/variables";
import { clearAuthAndLogout } from "../../globalContext/auth/authActions";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import { DownloadOutlined } from "@ant-design/icons";
import { jsPDF } from "jspdf";
import { toCanvas } from "html-to-image";
import { QuestionnaireContext } from "../../globalContext/questionnaire/questionnaireProvider";
import { setQuestionnaireVirtualHeight } from "../../globalContext/questionnaire/questionnaireActions";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { setSudoLoading } from "../../globalContext/globalLoader/globalLoaderAction";
import { StorageService } from "../../services";
import ConfirmModalContainer from "../../containers/ConfirmModalContainer/ConfirmModalContainer";
import {
  AUDIT_LINK_TOKEN_EXPIRE,
  AUDIT_MEDIUM_NOT_MATCH,
  AUDIT_ON_HOLD,
  NO_QUESTIONNAIRE_ASSIGNED,
} from "../../constants/errorConstants";
import moment from "moment";

const AddQuestionnaireViewStyle = {
  wrapperStyle: {
    backgroundColor: variables.grey5,
    padding: variables.paddingMedium,
    paddingTop: variables.paddingSmallLG,
    paddingBottom: variables.paddingMedium,
    paddingRight: 0,
    gap: variables.gapSmall,
    position: "relative",
  },
  bottomSectionStyle: (showMessage) => ({
    justifyContent: showMessage && variables.alignCenter,
    alignItems: showMessage && variables.alignCenter,
    height: variables.questionnaireBottomContainerHeight,
  }),
  topSectionStyle: {
    justifyContent: "space-between",
    paddingRight: variables.paddingMedium,
    position: "relative",
  },
  pdfBtn: (responsive) => ({
    border: `1px solid ${variables.grey4}`,
    fontSize: variables.fontSizeSmall,
    fontWeight: variables.fontWeightNormal,
    position: "absolute",
    right: responsive.isSm ? variables.paddingMedium : "5.5rem",
    backgroundColor: variables.bodyBg,
    top: responsive.isSm ? "-0.375rem" : "-0.7rem",
  }),
  msgColor: { color: variables.successGreen },
};

const AddQuestionnaireView = () => {
  const responsive = useResponsive();
  const navigate = useNavigate();
  const intl = useIntl();
  let { id, token, auditMedium } = useParams();
  token = decodeURIComponent(token);

  const [mode, setMode] = useState();
  const [showModal, setShowModal] = useState(false);
  const [labelText, setLabelText] = useState(
    "manageQuestionnaire.addNewQuestionnaire"
  );
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [authState, authDispatch] = useContext(AuthContext);
  const [auditState] = useContext(AuditContext);
  const [questionnaireState, questionnaireDispatch] =
    useContext(QuestionnaireContext);

  const isQuestionnaire = window.location.href.includes("questionnaire");
  const isAudit = getIsAudit();
  const isAuditPreview = window.location.href.includes("audit-preview");
  const isDnsd = window.location.href.includes("dnsd");

  useEffect(() => {
    if (isQuestionnaire) {
      if (!id) {
        setMode("add");
        setLabelText("manageQuestionnaire.addNewQuestionnaire");
      } else {
        setMode(window.location.href.includes("view") ? "view" : "edit");
        setLabelText(
          window.location.href.includes("view")
            ? "manageQuestionnaire.viewQuestionnaire"
            : "manageQuestionnaire.editQuestionnaire"
        );
      }
    } else {
      setMode("edit");
      setLabelText(
        getLabelText(
          isAudit,
          isDnsd ? 4 : authState?.currentRole?.id,
          auditMedium
        )
      );
    }
  }, [id, token, authState]);

  useEffect(() => {
    if (token && isDnsd && authState?.currentRole?.id) {
      setShowModal(true);
    } else {
      StorageService.set("public-token", token);
    }
  }, [token]);

  const onConfirm = () => {
    authDispatch(clearAuthAndLogout());
    StorageService.removeAll();
    StorageService.set("public-token", token);
    setShowModal(false);
  };

  function handleNavigation(e) {
    e.preventDefault();
    if (!isDnsd)
      navigate(isAudit || isAuditPreview ? "/audit-listing" : "/questionnaire");
  }

  function downloadPdf() {
    const input = document.getElementById("questionnaire");
    const section = document.getElementById("section");
    section.style.overflow = "hidden";

    // Calculate the total height of the scrollable content
    const totalHeight = input.scrollHeight;
    const totalWidth = input.scrollWidth;

    // Create a canvas element to use as a temporary container
    const canvas = document.createElement("canvas");
    canvas.width = totalWidth;
    canvas.height = totalHeight;

    const context = canvas.getContext("2d");

    // Keep track of the current position for rendering
    let currentPosition = 0;

    // Create a function to render each section of the content
    function renderContent() {
      toCanvas(input, {
        y: currentPosition,
        x: 0,
        width: totalWidth,
        height: totalHeight,
        scrollY: -window.scrollY,
      }).then((sectionCanvas) => {
        context.drawImage(sectionCanvas, 0, currentPosition);

        currentPosition += sectionCanvas.height;

        // Check if there's more content to render
        if (currentPosition < totalHeight) {
          renderContent(); // Recursive call to render next section
        } else {
          // All content is rendered, generate PDF
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "pt", [totalWidth, totalHeight]);
          const padding = 24; // 10pt padding
          pdf.addImage(
            imgData,
            "PNG",
            padding,
            padding,
            totalWidth - padding,
            totalHeight - padding * 2
          );
          pdf.save(getFileName());
          questionnaireDispatch(setQuestionnaireVirtualHeight(false));
          loaderDispatch(setSudoLoading(false));
        }
      });
    }

    // Start rendering the content
    renderContent();
  }

  function getFileName() {
    return `${auditState?.auditDetail?.[0]?.auditType}-${
      auditState?.auditDetail?.[0]?.productSubCategoryName || ""
    }-${moment().format(
      getLocalizedMessage(intl, "auditListing.displayDateFormat")
    )}.pdf`;
  }

  useEffect(() => {
    if (questionnaireState.isVirtualHeightNeeded) {
      setTimeout(() => {
        downloadPdf();
      }, 1000);
    } else {
      const section = document.getElementById("section");
      if (section) section.style.overflow = "auto";
    }
  }, [questionnaireState.isVirtualHeightNeeded]);

  const isTokenExpired = auditState?.auditErrorCode === AUDIT_LINK_TOKEN_EXPIRE;
  const isAuditOnHold = auditState?.auditErrorCode === AUDIT_ON_HOLD;
  const isQuestionnaireNotAssigned =
    auditState?.auditErrorCode === NO_QUESTIONNAIRE_ASSIGNED;
  const isAuditMediumNotSelf =
    auditState?.auditErrorCode === AUDIT_MEDIUM_NOT_MATCH;

  const showMessage =
    (isDnsd && auditState?.auditDetail?.auditStatus === "Audit Submitted") ||
    isTokenExpired ||
    isAuditOnHold ||
    isAuditMediumNotSelf ||
    isQuestionnaireNotAssigned;

  const onCancel = () => {
    window.close();
  };

  const renderBottomSection = () => {
    if (showMessage) {
      return (
        <Title style={AddQuestionnaireViewStyle.msgColor}>
          {getDisplayMessage({
            isTokenExpired,
            isAuditOnHold,
            intl,
            isAuditMediumNotSelf,
            isQuestionnaireNotAssigned,
          })}
        </Title>
      );
    }
    if (responsive?.isSm || isAudit || isAuditPreview) {
      return <AddQuestionnaireMainContainer mode={mode} />;
    }
    return <AddQuestionnaireMobileContainer />;
  };

  if (showModal)
    return (
      <ConfirmModalContainer
        open={showModal}
        setOpen={setShowModal}
        onCancel={onCancel}
        onClose={onCancel}
        onConfirm={onConfirm}
        question={getLocalizedMessage(intl, "dnsd.popup.msg")}
        closeInternally={false}
      />
    );

  return (
    <>
      <TwoRow
        style={AddQuestionnaireViewStyle.wrapperStyle}
        topSectionStyle={AddQuestionnaireViewStyle.topSectionStyle}
        topSection={
          <>
            {!showMessage && (
              <Link
                onClick={handleNavigation}
                type="backNavigation"
                iconSrc={!isDnsd && "/icons/arrow_back.svg"}
                labelText={getLocalizedMessage(intl, labelText)}
              />
            )}
            {false && (
              <Button
                onClick={() => {
                  questionnaireDispatch(setQuestionnaireVirtualHeight(true));
                  loaderDispatch(setSudoLoading(true));
                }}
                icon={<DownloadOutlined />}
                type="text"
                style={AddQuestionnaireViewStyle.pdfBtn(responsive)}
              >
                {getLocalizedMessage(intl, "auditListing.downloadPdf")}
              </Button>
            )}
          </>
        }
        bottomSection={renderBottomSection()}
        bottomSectionStyle={AddQuestionnaireViewStyle.bottomSectionStyle(
          showMessage
        )}
      />
    </>
  );
};

export default AddQuestionnaireView;

function getDisplayMessage({
  isTokenExpired,
  isAuditOnHold,
  intl,
  isAuditMediumNotSelf,
  isQuestionnaireNotAssigned,
}) {
  let messageKey;

  if (isTokenExpired) {
    messageKey = "dnsd.selfAudit.linkExpired";
  } else if (isAuditOnHold) {
    messageKey = "dnsd.selfAudit.onHold";
  } else if (isAuditMediumNotSelf) {
    messageKey = "dnsd.selfAudit.auditMediumNotSelf";
  } else if (isQuestionnaireNotAssigned) {
    messageKey = "dnsd.selfAudit.questionnaireNotAssigned";
  } else {
    messageKey = "dnsd.selfAudit.submittedMsg";
  }

  return getLocalizedMessage(intl, messageKey);
}
